import { FormattedDate, FormattedMessage } from 'react-intl';
import { PaloColumn, PaloListView } from '@synergy/components-palo';
import { PoolButton, ValueOrNDash } from '@synergy/components';
import { Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import AddIcon from '@mui/icons-material/AddCircleOutline';

import { BankAccountFormValues } from '../../models/bank-account-form-values';

const useStyles = makeStyles((theme: Theme) => ({
    titleContainer: {
        display: 'flex',
        flexDirection: 'column',
    },
    titleDetails: {
        display: 'flex',
        gap: theme.spacing(),
    },
    titleType: {
        color: theme.palette.grey[700],
    },
    titleMainAddress: {
        color: theme.palette.primary.main,
    },
    document: {
        color: theme.palette.primary.main,
        textDecoration: 'underline',
        cursor: 'pointer',
    },
}));

interface BankAccountsTableProps {
    organizationId?: number;
    bankAccounts: BankAccountFormValues[];
    onNewBankAccount: () => void;
    onBankAccountEdit: (bankAccount: BankAccountFormValues) => void;
    onBankAccountDelete: (id: number) => void;
}

export const BankAccountsTable = (props: BankAccountsTableProps) => {
    const { organizationId, bankAccounts, onBankAccountDelete, onBankAccountEdit, onNewBankAccount } = props;
    const classes = useStyles();

    const handleBankAccountDelete = (id?: number) => {
        if (id) {
            onBankAccountDelete(id);
        }
    };

    const columns: PaloColumn<BankAccountFormValues>[] = [
        {
            key: 'iban',
            type: 'name',
            // Widths for half a page: with the list widths the table ran to 946
            // pixels beside the notifications and scrolled sideways (R-205).
            width: '210px',
            head: <FormattedMessage id="app.table.column.iban" />,
            cell: bankAccount => <ValueOrNDash value={bankAccount.iban} />,
        },
        {
            key: 'bic',
            type: 'classification',
            width: '110px',
            head: <FormattedMessage id="app.table.column.bic" />,
            cell: bankAccount => <ValueOrNDash value={bankAccount.bic} />,
        },
        {
            key: 'customerReference',
            type: 'classification',
            width: '120px',
            head: <FormattedMessage id="app.table.column.customerReference" />,
            cell: bankAccount => <ValueOrNDash value={bankAccount.customerReference} />,
        },
        {
            key: 'mandateDate',
            type: 'date',
            width: '100px',
            head: <FormattedMessage id="app.table.column.mandateDate" />,
            cell: bankAccount => (
                <ValueOrNDash>
                    {bankAccount.mandateDate && (
                        <FormattedDate value={bankAccount.mandateDate} year="numeric" month="short" day="2-digit" />
                    )}
                </ValueOrNDash>
            ),
        },
        {
            key: 'document',
            // A count of files, not a name.
            type: 'count',
            head: <FormattedMessage id="app.table.column.document" />,
            cell: bankAccount =>
                bankAccount.media.length > 0 ? (
                    <span className={classes.document} onClick={() => onBankAccountEdit(bankAccount)}>
                        {bankAccount.media.length}
                    </span>
                ) : (
                    <>&ndash;</>
                ),
        },
    ];

    return (
        <>
            {bankAccounts.length > 0 && (
                <PaloListView<BankAccountFormValues, number>
                    embedded={true}
                    columns={columns}
                    items={bankAccounts}
                    // An account the user has just added has no id yet.
                    getRowId={bankAccount => bankAccount.id ?? -1 - bankAccounts.indexOf(bankAccount)}
                    emptyLabel={<FormattedMessage id="app.contactsAndOrganizations.detail.addBankAccount" />}
                    rowActions={bankAccount => [
                        {
                            label: <FormattedMessage id="app.common.edit" />,
                            onClick: () => onBankAccountEdit(bankAccount),
                        },
                        {
                            label: <FormattedMessage id="app.common.delete" />,
                            onClick: () => handleBankAccountDelete(bankAccount.id),
                        },
                    ]}
                />
            )}

            <PoolButton
                styleType="thirdLevel"
                startIcon={<AddIcon color="primary" />}
                onClick={onNewBankAccount}
                disabled={organizationId ? false : true}
            >
                <FormattedMessage
                    id={
                        organizationId
                            ? 'app.contactsAndOrganizations.detail.addBankAccount'
                            : 'app.contactsAndOrganizations.detail.saveBeforeAddBankData'
                    }
                />
            </PoolButton>
        </>
    );
};
